import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import {
  IconDropdownCollapse,
  IconDropdownExpand
} from '@contrast-security-inc/design-system-foundations'
import { Skeleton } from '@contrast-security-inc/design-system-skeletons'
import { Text, Link, LinkProps } from '@contrast-security-inc/design-system-typography'

const treeListCss = (depth: number) => css`
  list-style: none;
  padding: 0;
  margin: ${depth == 0 ? '0' : '0 0 0 16px'};
`
interface TreeListProps {
  subKey?: string
  items: unknown[]
  ItemComponent: React.FC<unknown>
  depth?: number
  parent?: unknown | null
}

const TreeList = ({
  subKey = 'children',
  items,
  ItemComponent,
  depth = 0,
  parent = null
}: TreeListProps) => {
  return (
    <ul className={treeListCss(depth)}>
      {items.map((item, index) => {
        return (
          <React.Fragment key={index}>
            <li
              className={css`
                word-break: break-all;
              `}
            >
              {/** @ts-ignore we may not know the children property */}
              <ItemComponent index={index} parent={parent} {...item} />
            </li>
            {/** @ts-ignore we may not know the children property */}
            {item[subKey]?.length > 0 ? (
              <TreeList
                parent={item}
                depth={depth + 1}
                // @ts-ignore
                items={item[subKey]}
                ItemComponent={ItemComponent}
              />
            ) : null}
          </React.Fragment>
        )
      })}
    </ul>
  )
}

export const ExpandableListEmpty = () => (
  <Text>{useIntl().formatMessage({ id: 'detail_emptyListDefaultMessage' })}</Text>
)

export const ListLoading = () => (
  // fragments here only to satisfy typescript ...
  <>
    {[...Array(5)].map((_, index) => (
      <Skeleton
        key={index}
        shape="rectangle"
        height={24}
        width="100%"
        style={{ marginBottom: '8px' }}
      />
    ))}
  </>
)

interface ExpandableListProps {
  ItemComponent: React.FC<unknown>
  listItems: unknown[]
  subKey?: string
  isLoading?: boolean
  showAll?: boolean
  EmptyListComponent?: React.FC<unknown>
  LoadingComponent?: () => React.JSX.Element
  defaultItemsDisplayed?: number
  maxHeight?: string
  viewAllLinkProps?: LinkProps
}

export const ExpandableList = ({
  // required
  listItems = [],
  ItemComponent,
  // options
  subKey = 'children',
  isLoading = false,
  EmptyListComponent = ExpandableListEmpty,
  LoadingComponent = ListLoading,
  defaultItemsDisplayed = 10,
  showAll = false,
  maxHeight = 'none',
  viewAllLinkProps
}: ExpandableListProps) => {
  const intl = useIntl()
  const allCount = listItems.length
  const [displayCount, setDisplayCount] = React.useState(showAll ? Infinity : defaultItemsDisplayed)
  const onClickView = React.useCallback(() => {
    if (displayCount < allCount) {
      setDisplayCount(Infinity)
    } else {
      setDisplayCount(defaultItemsDisplayed)
    }
  }, [displayCount, allCount, setDisplayCount, defaultItemsDisplayed])
  const itemsDisplayed = listItems.filter((_, index) => index < displayCount)
  const [viewMsg, ViewIcon] =
    displayCount < allCount
      ? [intl.formatMessage({ id: 'detail_viewAll' }, { count: allCount }), IconDropdownExpand]
      : [intl.formatMessage({ id: 'detail_viewLess' }), IconDropdownCollapse]

  return isLoading ? (
    <LoadingComponent />
  ) : (
    <>
      <div
        data-testid="expandable-list-list-container"
        className={css`
          overflow: auto;
          max-height: ${maxHeight};
        `}
      >
        {itemsDisplayed.length > 0 ? (
          <TreeList subKey={subKey} items={itemsDisplayed} ItemComponent={ItemComponent} />
        ) : (
          <EmptyListComponent />
        )}
      </div>
      {allCount > defaultItemsDisplayed && (
        <Link
          font="body"
          className={css`
            display: flex;
            align-items: center;
          `}
          onClick={onClickView}
          {...viewAllLinkProps}
        >
          {viewMsg}
          <ViewIcon color="turquoise70" />
        </Link>
      )}
    </>
  )
}
